import { createServerFn } from "@tanstack/react-start";

function parseRate(value: string | undefined, fallback: number): number {
  if (!value) return fallback;
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(1, Math.max(0, n));
}

function parseBool(value: string | undefined): boolean {
  return value === "1" || value?.toLowerCase() === "true";
}

/**
 * Resolve the effective error sample rate. Incident mode forces 1.0 so on-call
 * sees every event without needing a rebuild.
 */
export function resolveSentryRates(env: NodeJS.ProcessEnv = process.env) {
  const isProd = (env.NODE_ENV ?? "production") === "production";
  const incident = parseBool(env.SENTRY_INCIDENT_MODE);
  const baseSample = parseRate(env.SENTRY_SAMPLE_RATE, isProd ? 0.1 : 1);
  const baseTraces = parseRate(env.SENTRY_TRACES_SAMPLE_RATE, 0);
  return {
    incident,
    sampleRate: incident ? 1 : baseSample,
    tracesSampleRate: incident ? Math.max(baseTraces, 0.2) : baseTraces,
  };
}

// Expose Sentry DSN + release/env to the browser at runtime. The DSN is a
// public identifier (safe to ship to clients), but we keep it in a runtime
// secret so it can be rotated without rebuilds.
export const getSentryConfig = createServerFn({ method: "GET" }).handler(
  async () => {
    const rates = resolveSentryRates();
    return {
      dsn: process.env.SENTRY_DSN ?? "",
      release: process.env.SENTRY_RELEASE ?? process.env.CF_VERSION_METADATA_ID ?? "dev",
      environment: process.env.SENTRY_ENVIRONMENT ?? (process.env.NODE_ENV ?? "production"),
      sampleRate: rates.sampleRate,
      tracesSampleRate: rates.tracesSampleRate,
      incident: rates.incident,
    };
  },
);